// src/pages/LinkFlow.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import StageFrame from '../components/StageFrame';
import FullBleed from '../components/FullBleed';
import bg from '../assets/card.png';
import cardApply from '../assets/card_apply.png';
import PhoneInput from '../components/PhoneInput';
import {
  buildCouponClaimPath,
  extractCouponCodeFromReturnUrl,
  savePendingClaimResult,
} from '../utils/couponLiff';

axios.defaults.withCredentials = true;

const TAB_POS = { x: 70, y: 250, w: 940 };
const LIFF_SDK_URL = 'https://static.line-scdn.net/liff/edge/2/sdk.js';
const HARD_CODED_LIFF_ID = '2007543734-5n4NpyXV';

const getLiffId = () =>
  (process.env.REACT_APP_LIFF_ID || window.__LIFF_ID__ || HARD_CODED_LIFF_ID).trim();

function ensureLiffSdk() {
  if (window.liff) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existed = document.querySelector(`script[src="${LIFF_SDK_URL}"]`);
    if (existed) {
      existed.addEventListener('load', () => resolve());
      existed.addEventListener('error', () => reject(new Error('LOAD_LIFF_SDK_FAIL')));
      return;
    }
    const s = document.createElement('script');
    s.src = LIFF_SDK_URL;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('LOAD_LIFF_SDK_FAIL'));
    document.head.appendChild(s);
  });
}

const digits = (s='') => (s.match(/\d+/g)?.join('') || '');

export default function LinkFlow() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [name,  setName ] = useState('');
  const [birthday, setBirthday] = useState(''); // YYYY-MM-DD
  const [err, setErr] = useState('');
  const [needName, setNeedName] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lineSub, setLineSub] = useState(null); // ★ 這次真的抓到的 LINE sub

  const phoneRef = useRef(null);
  const emailRef = useRef(null);

  const liffId = useMemo(getLiffId, []);
  const deepLink = useMemo(() => {
    const returnTo = window.location.pathname + (window.location.search || '');
    return `line://app/${liffId}?liff.state=${encodeURIComponent(returnTo)}`;
  }, [liffId]);

  // 如果從 /link?mode=register 來，轉成 /link（保持你原邏輯）
  useEffect(() => {
    const usp = new URLSearchParams(window.location.search);
    if (usp.get('mode') === 'register') navigate('/link', { replace: true });
  }, [navigate]);

  // 進頁先嘗試「真正」拿一次 sub（LIFF → 解碼 idToken）
  useEffect(() => {
    (async () => {
      try {
        setBusy(true);
        await ensureLiffSdk();
        const liff = window.liff;
        // 用 withLoginOnExternalBrowser: true，iOS/Safari 回來也取得到 context
        await liff.init({ liffId, withLoginOnExternalBrowser: true });

        // 只要在 LINE 內 or LIFF 已登入，就能拿 token
        const inClient =
          (typeof liff.isInClient === 'function' && liff.isInClient() === true) ||
          (liff.getContext?.()?.type && liff.getContext().type !== 'external');

        if (inClient && !liff.isLoggedIn?.()) {
          // 在 LINE 內但還沒登入 LIFF，就做一次登入再回來
          liff.login({ redirectUri: window.location.href });
          return;
        }

        // const token = liff.getIDToken?.(); // 未使用 → 移除避免 ESLint 警告
        const decoded = liff.getDecodedIDToken?.();
        const sub = decoded?.sub || null;

        if (sub) {
          setLineSub(sub);
          localStorage.setItem('line_sub', sub); // 同步一份備用
        } else {
          // 沒拿到 → 用之前存的當備援
          const backup = localStorage.getItem('line_sub');
          if (backup) setLineSub(backup);
        }
      } catch {
        // 完全沒有 LIFF（瀏覽器）或初始化失敗 → 嘗試用 localStorage
        const backup = localStorage.getItem('line_sub');
        if (backup) setLineSub(backup);
      } finally {
        setBusy(false);
        // 自動 focus
        setTimeout(() => (!needName ? phoneRef.current : emailRef.current)?.focus?.(), 50);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liffId]);

  const getReturnUrl = () => {
    const usp = new URLSearchParams(window.location.search);
    const from = usp.get('from');
    if (from) {
      try {
        const decoded = decodeURIComponent(from);
        if (decoded.startsWith('http')) {
          const url = new URL(decoded);
          return url.pathname + url.search;
        }
        return decoded;
      } catch (e) {
        console.warn('Failed to decode from param:', e);
        return from;
      }
    }
    return '/card';
  };

  const getCouponCodeFromContext = () => extractCouponCodeFromReturnUrl(getReturnUrl());

  const finishWithOptionalClaim = (data) => {
    const returnUrl = getReturnUrl();
    const couponCode = getCouponCodeFromContext();
    if (data?.claim && couponCode) {
      savePendingClaimResult(couponCode, data.claim);
      navigate(returnUrl, { replace: true });
      return;
    }
    if (couponCode) {
      navigate(buildCouponClaimPath(couponCode, { lineClaim: true }), { replace: true });
      return;
    }
    navigate(returnUrl, { replace: true });
  };

  const onSubmitPhone = async (e) => {
    e.preventDefault();
    setErr('');

    const p = digits(phone);
    if (p.length < 8) {
      setErr('請輸入正確電話（至少 8 碼數字）');
      return;
    }

    // ★ 硬規則：若這頁是從 LINE 進來卻沒有 lineSub，就請他用 LINE 重新開啟
    if (!lineSub) {
      setErr('未取得 LINE 身分，請點上方「在 LINE 中開啟」或改用電話 + PIN。');
      return;
    }

    try {
      setBusy(true);
      const couponCode = getCouponCodeFromContext();
      const couponLang = (() => {
        try { return localStorage.getItem('coupon:lang') || 'zh-TW'; } catch { return 'zh-TW'; }
      })();
      const { data } = await axios.post('/api/member/link', {
        phone: p,
        line_sub: lineSub,
        coupon_code: couponCode || undefined,
        lang: couponLang,
      });
      setBusy(false);
      if (data?.found) {
        finishWithOptionalClaim(data);
      } else {
        setNeedName(true);
      }
    } catch (e2) {
      setBusy(false);
      if (e2?.response?.status === 404) {
        setNeedName(true);
      } else if (e2?.response?.status === 409) {
        setErr('此電話已綁定其他 LINE，請用該 LINE 登入或改用電話 + PIN。');
      } else {
        setErr('連結失敗，請稍後再試');
      }
    }
  };

  const onSubmitRegister = async (e) => {
    e.preventDefault();
    setErr('');

    // 移除格式限制：接受任何格式的電話號碼（包括外國號碼如 821067808260）
    const p = String(phone || '').trim();
    if (!p) return setErr('請輸入電話');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setErr('Email 格式不正確');
    if (!name.trim()) return setErr('請輸入姓名');

    if (!lineSub) {
      setErr('未取得 LINE 身分，請點上方「在 LINE 中開啟」或改用電話 + PIN。');
      return;
    }

    try {
      setBusy(true);
      const couponCode = getCouponCodeFromContext();
      const couponLang = (() => {
        try { return localStorage.getItem('coupon:lang') || 'zh-TW'; } catch { return 'zh-TW'; }
      })();
      const { data } = await axios.post('/api/member/register', {
        email, phone: p, name, line_sub: lineSub,
        birthday: birthday || undefined,
        coupon_code: couponCode || undefined,
        lang: couponLang,
      });
      setBusy(false);
      finishWithOptionalClaim(data);
    } catch (e2) {
      setBusy(false);
      setErr(e2?.response?.status === 409 ? '此電話或 Email 已註冊' : '註冊失敗，請重試');
    }
  };

  return (
    <FullBleed bg={bg}>
      <StageFrame
        designW={1080}
        designH={1920}
        maxPcWidth={440}
        overlay={({ px }) => (
          <>
            <style>{`
              .apply-tab { position: absolute; left: ${px(TAB_POS.x)}px; top: ${px(TAB_POS.y)}px; width: ${px(TAB_POS.w)}px; height: auto; pointer-events: none; }
              .lf-wrap { position:absolute; left:${px(140)}px; right:${px(140)}px; top:${px(500)}px; color:#111; }
              .lf-h1 { font-family:"CreamFont","CSong3HK",system-ui,-apple-system,sans-serif; font-weight:800; font-size:${px(70)}px; line-height:1.12; margin:0 0 ${px(10)}px; }
              .lf-p  { font-family:"CSong3HK",system-ui,-apple-system,sans-serif; font-size:${px(36)}px; margin:0 0 ${px(18)}px; color:#222; }
              .lf-row{ display:grid; gap:${px(12)}px; margin:${px(14)}px 0 ${px(18)}px; }
              .lf-inp{ width:100%; box-sizing:border-box; font-family:"CSong3HK",system-ui,-apple-system,sans-serif; font-size:${px(44)}px; padding:${px(14)}px ${px(6)}px; background:transparent; color:#111; border:none; border-bottom:2px solid rgba(0,0,0,.85); outline:none; }
              .lf-inp::placeholder { color:rgba(0,0,0,.45); }
              .lf-btn{ width:100%; box-sizing:border-box; font-family:"CreamFont","CSong3HK",system-ui,-apple-system,sans-serif; font-size:${px(42)}px; font-weight:900; padding:${px(14)}px; border-radius:${px(10)}px; border:1px solid #111; background:transparent; color:#111; cursor:pointer; }
              .lf-btn:active { transform:translateY(1px); }
              .lf-err{ display:inline-block; margin-top:${px(12)}px; background:#fff1f0; color:#b00020; padding:${px(8)}px ${px(10)}px; border-radius:${px(10)}px; font-weight:800; font-size:${px(28)}px; }
              .lf-small { font-size:${px(28)}px; opacity:.7; }
              .lf-links{ display:flex; gap:${px(12)}px; margin-top:${px(10)}px; }
              .lf-link{ font-size:${px(28)}px; text-decoration:underline; cursor:pointer; opacity:.8; }
            `}</style>

            <img className="apply-tab" src={cardApply} alt="" />

            <div className="lf-wrap">
              {!needName ? (
                <form onSubmit={onSubmitPhone}>
                  <h1 className="lf-h1">會員登入／綁定</h1>
                  <p className="lf-p">請輸入你的聯絡電話</p>
                  <div className="lf-row">
                    <div style={{ width: '100%', maxWidth: px(760), margin: '0 auto' }}>
                      <PhoneInput
                        value={phone}
                        onChange={(val) => {
                          setPhone(val);
                          if (needName) setNeedName(false);
                        }}
                        placeholder="例：912345678"
                        selectStyle={{
                          padding: `${px(16)}px ${px(8)}px`,
                          fontSize: `${px(40)}px`,
                          borderRadius: `${px(16)}px 0 0 ${px(16)}px`,
                          border: `${px(2)}px solid #111`,
                          borderRight: 'none',
                          background: '#fff',
                          minWidth: 90,
                          boxSizing: 'border-box',
                        }}
                        inputStyle={{
                          flex: 1,
                          minWidth: 0,
                          padding: `${px(16)}px ${px(12)}px`,
                          fontSize: `${px(46)}px`,
                          borderRadius: `0 ${px(16)}px ${px(16)}px 0`,
                          border: `${px(2)}px solid #111`,
                          background: '#fff',
                          fontWeight: 700,
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <span className="lf-small">選擇國碼後輸入電話；台灣請從 9 開始（可輸入 09，系統會自動轉換）</span>
                  </div>
                  <button className="lf-btn" type="submit" disabled={busy}>
                    {busy ? '處理中…' : '下一步'}
                  </button>
                  <div className="lf-links">
                    {/* 若不在 LINE 內或 sub 拿不到，提供兩個退路 */}
                    {!lineSub && (
                      <>
                        <span className="lf-link" onClick={() => window.location.replace(deepLink)}>
                          在 LINE 中開啟
                        </span>
                        <span className="lf-link" onClick={() => {
                          const usp = new URLSearchParams(window.location.search);
                          const from = usp.get('from');
                          const fromParam = from ? `?from=${encodeURIComponent(from)}` : '';
                          window.location.assign(`/pass-login${fromParam}`);
                        }}>
                          改用電話 + PIN
                        </span>
                      </>
                    )}
                  </div>
                  {err && <div className="lf-err">⚠️ {err}</div>}
                </form>
              ) : (
                <form onSubmit={onSubmitRegister}>
                  <h1 className="lf-h1">快速註冊</h1>
                  <p className="lf-p">查無此電話，請補上資料完成註冊</p>
                  <div className="lf-row">
                    <input className="lf-inp" value={phone} readOnly />
                    <input
                      ref={emailRef}
                      className="lf-inp"
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      autoComplete="email"
                      required
                    />
                    <input
                      className="lf-inp"
                      placeholder="姓名 / Name"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      autoComplete="name"
                      required
                    />
                    <input
                      className="lf-inp"
                      type="date"
                      placeholder="生日（YYYY-MM-DD）"
                      value={birthday}
                      onChange={e => setBirthday(e.target.value)}
                    />
                    <span className="lf-small">生日可留空，之後也能補</span>
                  </div>
                  <button className="lf-btn" type="submit" disabled={busy}>
                    {busy ? '處理中…' : '註冊並進入'}
                  </button>
                  {!lineSub && (
                    <div className="lf-links">
                      <span className="lf-link" onClick={() => window.location.replace(deepLink)}>
                        在 LINE 中開啟
                      </span>
                      <span className="lf-link" onClick={() => {
                        const usp = new URLSearchParams(window.location.search);
                        const from = usp.get('from');
                        const fromParam = from ? `?from=${encodeURIComponent(from)}` : '';
                        window.location.assign(`/pass-login${fromParam}`);
                      }}>
                        改用電話 + PIN
                      </span>
                    </div>
                  )}
                  {err && <div className="lf-err">⚠️ {err}</div>}
                </form>
              )}
            </div>
          </>
        )}
      />
    </FullBleed>
  );
}
